import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Axios from "axios";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import { safeJwtDecode } from "../../utilities/tokenUtils";
import UserContext from "../../context/UserContext";

const schema = yup.object().shape({
  email: yup
    .string()
    .required("Email es requerido")
    .email("Ingrese un email válido")
    .max(100, "Email no puede exceder 100 caracteres"),
  password: yup
    .string()
    .required("Contraseña es requerida")
    .min(14, "Mínimo 14 caracteres")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Debe contener mayúscula, minúscula y número"
    )
    .matches(
      /[!@#$%^&*(),.?":{}|<>\-_+=[\]\\/]/,
      "Debe incluir al menos un carácter especial"
    ),
  passwordCheck: yup
    .string()
    .required("Confirma la contraseña")
    .oneOf([yup.ref("password"), null], "Las contraseñas deben coincidir"),
  displayName: yup
    .string()
    .required("Nombre de usuario es requerido")
    .min(2, "Mínimo 2 caracteres")
    .max(50, "Máximo 50 caracteres")
    .matches(/^[a-zA-Z0-9áéíóúñü\s]+$/, "Solo letras, números y espacios"),
  role: yup
    .string()
    .required("Rol es requerido")
    .oneOf(["administrador", "redactor"], "Rol no válido"),
});

export default function Register() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      role: "redactor",
    },
  });

  const { userData } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userData.token) {
      navigate("/admin/login");
    } else {
      if (safeJwtDecode(userData.token).role !== "administrador") navigate("/");
    }
  }, [navigate, userData.token]);

  const decoded = safeJwtDecode(userData.token);
  const userRole = decoded.role ?? null;
  const userDisplayName = decoded.displayName ?? null;

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      if (userData?.token) {
        const sanitizedData = {
          email: DOMPurify.sanitize(data.email),
          password: DOMPurify.sanitize(data.password),
          passwordCheck: DOMPurify.sanitize(data.passwordCheck),
          displayName: DOMPurify.sanitize(data.displayName),
          role: data.role,
          creator: userDisplayName,
        };

        await Axios.post(
          `${process.env.REACT_APP_BACK_DOMAIN}/users/register`,
          sanitizedData,
          {
            headers: {
              "x-auth-token": userData.token,
              "Content-Type": "application/json",
            },
          }
        );

        toast.success("Usuario creado correctamente");
        reset();
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        toast.error(err.response.data.msg);
      } else {
        toast.error("Error al crear el usuario");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="container">
        <h2>Registrar Usuario</h2>

        {userRole === "administrador" && (
          <form className="Form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="Form-Field">
              <label className="Form-Label" htmlFor="email">
                Correo Electrónico:
              </label>
              <input
                autoComplete="off"
                className="Form-Input"
                id="email"
                type="email"
                placeholder="usuario@ejemplo.com"
                {...register("email")}
              />
              {errors.email && (
                <span style={{ color: "red" }}>{errors.email.message}</span>
              )}
            </div>

            <div className="Form-Field">
              <label className="Form-Label" htmlFor="password">
                Contraseña:
              </label>
              <input
                autoComplete="off"
                className="Form-Input"
                id="password"
                type="password"
                placeholder="Mín. 14 chars, mayúscula, minúscula, número y carácter especial"
                {...register("password")}
              />
              {errors.password && (
                <span style={{ color: "red" }}>{errors.password.message}</span>
              )}
            </div>

            <div className="Form-Field">
              <label className="Form-Label" htmlFor="passwordCheck">
                Confirmar Contraseña:
              </label>
              <input
                autoComplete="off"
                className="Form-Input"
                id="passwordCheck"
                type="password"
                placeholder="Repite la contraseña"
                {...register("passwordCheck")}
              />
              {errors.passwordCheck && (
                <span style={{ color: "red" }}>{errors.passwordCheck.message}</span>
              )}
            </div>

            <div className="Form-Field">
              <label className="Form-Label" htmlFor="displayName">
                Nombre de Usuario:
              </label>
              <input
                autoComplete="off"
                className="Form-Input"
                id="displayName"
                type="text"
                placeholder="Nombre completo del usuario"
                {...register("displayName")}
              />
              {errors.displayName && (
                <span style={{ color: "red" }}>{errors.displayName.message}</span>
              )}
            </div>

            <div className="Form-Field">
              <label className="Form-Label" htmlFor="role">
                Rol:
              </label>
              <select
                className="Form-Input"
                id="role"
                {...register("role")}
              >
                <option value="administrador">Administrador</option>
                <option value="redactor">Redactor</option>
              </select>
              {errors.role && (
                <span style={{ color: "red" }}>{errors.role.message}</span>
              )}
            </div>

            <input
              className="Button Button_primary"
              type="submit"
              value={isSubmitting ? "Creando usuario..." : "Registrar"}
              disabled={isSubmitting}
            />
          </form>
        )}
      </div>
    </div>
  );
}